import { usePrevious } from "@react-hookz/web";
import YouTube from "@vip30/react-youtube";
import { useAtom, useAtomValue } from "jotai";
import { useEffect } from "react";

import { onlyOnePlayingPlayerAtom } from "./atoms";

export const useOnlyOnePlayingPlayer = () => {
  const [{ adsManager, articleSpeechifyPlayer, youtubePlayer }, setOnlyOnePlayingPlayerAtom] =
    useAtom(onlyOnePlayingPlayerAtom);

  useEffect(() => {
    if (!adsManager) {
      return;
    }

    if (adsManager.getRemainingTime() > 0) {
      articleSpeechifyPlayer?.pause();
      void youtubePlayer?.pauseVideo();
    }

    const handleAdStarted = () => {
      articleSpeechifyPlayer?.pause();
      void youtubePlayer?.pauseVideo();
      setOnlyOnePlayingPlayerAtom(state => ({ ...state, stopYoutubeAutoplay: false }));
    };
    adsManager.addEventListener(google.ima.AdEvent.Type.STARTED, handleAdStarted);
    adsManager.addEventListener(google.ima.AdEvent.Type.RESUMED, handleAdStarted);
    return () => {
      adsManager.removeEventListener(google.ima.AdEvent.Type.STARTED, handleAdStarted);
      adsManager.removeEventListener(google.ima.AdEvent.Type.RESUMED, handleAdStarted);
    };
  }, [adsManager, articleSpeechifyPlayer, setOnlyOnePlayingPlayerAtom, youtubePlayer]);

  useEffect(() => {
    if (!articleSpeechifyPlayer) {
      return;
    }

    const run = async () => {
      const playerState = await articleSpeechifyPlayer.getPlayerState();
      if (!playerState.playing) return;
      adsManager?.pause();
      void youtubePlayer?.pauseVideo();
    };
    void run();
  }, [adsManager, articleSpeechifyPlayer, setOnlyOnePlayingPlayerAtom, youtubePlayer]);

  useEffect(() => {
    if (!youtubePlayer) {
      return;
    }

    // We don't want to pause the ima player when the youtube is playing because of the
    // possibility of the youtube player is playing in the background which is not expected by us
    const pauseOtherPlayerExceptAds = (playerState: number) => {
      if (
        playerState !== YouTube.PlayerState.PLAYING ||
        (adsManager && adsManager?.getRemainingTime() > 0)
      )
        return;
      articleSpeechifyPlayer?.pause();
    };

    const run = async () => {
      const playerState: number = await youtubePlayer.getPlayerState();
      pauseOtherPlayerExceptAds(playerState);
    };
    void run();

    const handleOnStateChange = (event: CustomEvent) => {
      const { data } = event as unknown as { data: number };
      pauseOtherPlayerExceptAds(data);
      setOnlyOnePlayingPlayerAtom(state => ({ ...state, stopYoutubeAutoplay: false }));
    };
    void youtubePlayer.addEventListener("onStateChange", handleOnStateChange);
    return () => {
      void youtubePlayer.removeEventListener("onStateChange", handleOnStateChange);
    };
  }, [adsManager, articleSpeechifyPlayer, setOnlyOnePlayingPlayerAtom, youtubePlayer]);
  useOnlyOneYoutubePlayerEffect();
};

const useOnlyOneYoutubePlayerEffect = () => {
  const { youtubePlayer } = useAtomValue(onlyOnePlayingPlayerAtom);

  const previousYoutubePlayer = usePrevious(youtubePlayer);

  useEffect(() => {
    const run = async () => {
      const previousVideoIframe = await previousYoutubePlayer?.getIframe();
      const currentVideoIframe = await youtubePlayer?.getIframe();
      if (!previousVideoIframe || !currentVideoIframe) return;
      else if (previousVideoIframe !== currentVideoIframe) {
        void previousYoutubePlayer?.pauseVideo();
      }
    };
    void run();
  }, [previousYoutubePlayer, youtubePlayer]);
};
